
import {PropType} from "vue";
import {CartService} from "../../../showcase/services/CartService";
import {OfferUniverse} from "../../../showcase/models/CartApiResponse";
import {SponsorCartRequest, SponsorCartResponse} from "../../../showcase/models/SponsorCartResponse";
import {InternetDiscountPopinDto} from "../../cart/models/InternetDiscountPopinDto";
import Lien from "../ob1/Lien.vue";
import PageLoadingSpinner from "../custom/PageLoadingSpinner.vue";
import DiscountPopinIntroShared from "./DiscountPopinIntroShared.vue";
import DiscountPopinFormShared from "./DiscountPopinFormShared.vue";
import DiscountPopinInfoShared from "./DiscountPopinInfoShared.vue";
import Bouton from "../ob1/Bouton.vue";
import {
    SubmissionResultType,
    TMS_LABEL_BY_RESULT,
    getPreviousView,
    validateDiscountFormValues
} from "./discountPopinUtils";

declare function updateUrlWithParam(url: string, paramName: string, paramValue: string): string;
declare function getParameterByName(name: string, url?: string): string | null;

type DiscountPopinMode = 'cart' | 'offers';

export default {
    name: "InternetDiscountPopinShared",
    components: {
        Lien,
        Bouton,
        PageLoadingSpinner,
        DiscountPopinIntroShared,
        DiscountPopinFormShared,
        DiscountPopinInfoShared
    },
    props: {
        id: {
            type: String,
            required: true,
        },
        dto: {
            type: Object as PropType<InternetDiscountPopinDto>,
            required: true
        },
        mode: {
            type: String as PropType<DiscountPopinMode>,
            required: true
        },
        offersList: {
            type: Array,
            required: false,
            default: () => []
        }
    },
    data() {
        return {
            activeView: 'intro',
            phoneLineValue: '',
            customerCodeValue: '',
            snackbarMessage: "",
            snackbarIcon: "icon-error-severe",
            isRefreshLoading: false,
            phoneNumberModelValue: "",
            phoneNumberErrorMessage: "",
            showAlertMessage: false,
            alertMessageTitle: "",
            phoneLineErrorMessage: "",
            customerCodeErrorMessage: "",
            sponsorSuccess: false,
            submissionResultType: null as SubmissionResultType,
            selectedOfferSeoId: null as string | null,
        }
    },
    computed: {
        isOffersMode(): boolean {
            return this.mode === 'offers';
        }
    },
    mounted() {
        const modalElement = document.getElementById(this.id);
        if (modalElement) {
            if (this.isOffersMode) {
                modalElement.addEventListener('show.bs.modal', this.onModalShow);
            }
            modalElement.addEventListener('hidden.bs.modal', this.onModalHidden);
        }
    },
    beforeDestroy() {
        const modalElement = document.getElementById(this.id);
        if (modalElement) {
            if (this.isOffersMode) {
                modalElement.removeEventListener('show.bs.modal', this.onModalShow);
            }
            modalElement.removeEventListener('hidden.bs.modal', this.onModalHidden);
        }
    },
    methods: {
        onModalShow(event: any) {
            const triggerElement = event && event.relatedTarget ? event.relatedTarget : null;
            this.selectedOfferSeoId = triggerElement && triggerElement.dataset ? triggerElement.dataset.offerSeoId || null : null;
        },
        onIntroAddOfferClick(redirectUrl: string) {
            if (!this.isOffersMode || !this.selectedOfferSeoId) {
                window.location.href = redirectUrl;
                return;
            }

            const returnUrl = getParameterByName('return_url', redirectUrl);
            if (!returnUrl) {
                window.location.href = redirectUrl;
                return;
            }

            let decodedReturnUrl;
            try {
                decodedReturnUrl = decodeURIComponent(returnUrl);
            } catch (e) {
                window.location.href = redirectUrl;
                return;
            }

            const returnUrlWithOffer = updateUrlWithParam(
                decodedReturnUrl,
                'offerId',
                this.selectedOfferSeoId
            );
            window.location.href = updateUrlWithParam(
                redirectUrl,
                'return_url',
                encodeURIComponent(returnUrlWithOffer)
            );
        },
        onIntroContinueWithoutDiscountClick(redirectUrl: string) {
            if (!this.isOffersMode || !this.selectedOfferSeoId) {
                window.location.href = redirectUrl;
                return;
            }

            const selectedFmrOffer = (this.offersList as any[]).find(
                offer => offer.idSeo === this.selectedOfferSeoId
            );

            if (!selectedFmrOffer || !selectedFmrOffer.coreOfferCode) {
                window.location.href = updateUrlWithParam(
                    redirectUrl,
                    'offerId',
                    this.selectedOfferSeoId
                );
                return;
            }

            const fmOffer = (this.offersList as any[]).find(
                offer => offer.coreOfferCode === selectedFmrOffer.coreOfferCode
                    && !offer.fmr
            );

            if (!fmOffer) {
                window.location.href = updateUrlWithParam(
                    redirectUrl,
                    'offerId',
                    this.selectedOfferSeoId
                );
                return;
            }

            this.isRefreshLoading = true;
            CartService.setCart({
                id: fmOffer.idSeo,
                device: null,
                mobileId: null,
                universe: OfferUniverse.MOBILE,
                needDevice: false
            }).then((response: any) => {
                const closeButton = document.querySelector(`#${this.id} [data-bs-dismiss="modal"]`) as HTMLElement;
                if (closeButton) {
                    closeButton.click();
                }
                window.location.href = response.redirectUrl;
            }).catch(() => {
                this.isRefreshLoading = false;
                window.location.href = updateUrlWithParam(
                    redirectUrl,
                    'offerId',
                    fmOffer.idSeo
                );
            });
        },
        trackSubmissionResult(result: Exclude<SubmissionResultType, null>) {
            this.submissionResultType = result;
            Tms.link({
                category: this.dto.formContent.formButton.tms.category,
                action: this.dto.formContent.formButton.tms.action,
                label: TMS_LABEL_BY_RESULT[result],
            });
        },
        labelForResult(result: SubmissionResultType): string | null {
            return result ? TMS_LABEL_BY_RESULT[result] : null;
        },
        goBack(event: Event) {
            if (event) {
                event.preventDefault();
            }
            this.activeView = getPreviousView(this.activeView);
        },
        onAddSponsorPid() {
            this.phoneLineErrorMessage = "";
            this.customerCodeErrorMessage = "";
            this.showAlertMessage = false;
            this.submissionResultType = null;

            if (!this.validateForm()) {
                this.trackSubmissionResult('bad_credentials');
                return false;
            }

            if (this.isOffersMode) {
                return this.addSponsorToSelectedOffer();
            }

            return this.addSponsorToCart();
        },
        addSponsorToCart() {
            this.isRefreshLoading = true;

            const cartSponsorRequest: SponsorCartRequest = {
                phoneLineValue: this.phoneLineValue,
                customerCodeValue: this.customerCodeValue,
            };

            CartService.addSponsor(cartSponsorRequest).then((response: SponsorCartResponse) => {
                if (response.type === 'success') {
                    this.trackSubmissionResult('success');
                    this.sponsorSuccess = true;
                    const closeButton = document.querySelector(`#${this.id} [data-bs-dismiss="modal"]`) as HTMLElement;
                    if (closeButton) {
                        closeButton.click();
                    }
                } else if (response.type === 'notFound') {
                    this.trackSubmissionResult('notFound');
                    this.showAlertMessage = true;
                } else if (response.type === 'badRequest') {
                    this.trackSubmissionResult('badRequest');
                    this.showAlertMessage = true;
                } else {
                    this.trackSubmissionResult('technical');
                    this.alertMessageTitle = "Une erreur technique est survenue. Merci de réessayer plus tard.";
                    this.showAlertMessage = true;
                }
            }).catch(() => {
                this.trackSubmissionResult('technical');
                window.location.href = this.dto.formContent.errorUrl;
            }).finally(() => {
                this.isRefreshLoading = false;
            });
        },
        addSponsorToSelectedOffer() {
            if (!this.selectedOfferSeoId) {
                this.trackSubmissionResult('technical');
                window.location.href = this.dto.formContent.errorUrl;
                return false;
            }

            this.isRefreshLoading = true;

            CartService.setCart({
                id: this.selectedOfferSeoId,
                device: null,
                mobileId: null,
                sponsorCartRequest: {
                    phoneLineValue: this.phoneLineValue,
                    customerCodeValue: this.customerCodeValue,
                },
                universe: OfferUniverse.MOBILE,
                needDevice: false,
            }, false).then((response: any) => {
                this.trackSubmissionResult('success');
                const closeButton = document.querySelector(`#${this.id} [data-bs-dismiss="modal"]`) as HTMLElement;
                if (closeButton) {
                    closeButton.click();
                }
                window.location.href = response.redirectUrl;
            }).catch((error: any) => {
                if (error && error.status === 404) {
                    this.trackSubmissionResult('notFound');
                    this.showAlertMessage = true;
                    return;
                }
                if (error && error.status === 400) {
                    this.trackSubmissionResult('badRequest');
                    this.showAlertMessage = true;
                    return;
                }
                if (error && error.status === 503) {
                    this.trackSubmissionResult('technical');
                    this.alertMessageTitle = "Une erreur technique est survenue. Merci de réessayer plus tard.";
                    this.showAlertMessage = true;
                    return;
                }
                this.trackSubmissionResult('technical');
                window.location.href = this.dto.formContent.errorUrl;
            }).finally(() => {
                this.isRefreshLoading = false;
            });
        },
        validateForm() {
            const result = validateDiscountFormValues(this.phoneLineValue, this.customerCodeValue);
            this.phoneLineErrorMessage = result.phoneLineErrorMessage;
            this.customerCodeErrorMessage = result.customerCodeErrorMessage;
            return result.isValid;
        },
        onModalHidden() {
            if (this.sponsorSuccess) {
                this.$emit('sponsor-success', {
                    snackBarAlert: {
                        message: this.dto.formContent.successMessage,
                        icon: 'icon-tick-circle',
                    },
                    successUrl: this.dto.formContent.successUrl,
                });
            }
            this.activeView = 'intro';
            this.sponsorSuccess = false;
            this.selectedOfferSeoId = null;
        },
    },
}
